import {
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Fade
} from '@mui/material';
import { FC, useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAllPartiesLedgerTransactions } from '../../store/reportStore/reportThunk';
import styled_ from "@emotion/styled";
import moment from 'moment';
import { Utils, VocuherCategory } from "../../utils/common-utils";
import ReusableReportHeader from './ReusableReportHeader';
import Filters from '../Filters/Filters';
import KeyboardArrowUp from '@mui/icons-material/ArrowUpward';
import KeyboardArrowDown from '@mui/icons-material/ArrowDownward';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({}));

const PartyLedgerJournal: FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);

  // Helper function to format voucher type labels
  const formatVoucherType = (voucherType: string): string => {
    if (!voucherType) return '';

    // Handle specific cases
    if (voucherType === 'Invoice') return 'Sale Invoice';
    if (voucherType === 'EXPENSE_PAYMENT') return 'Expense Payment';

    // Handle underscore-separated names (convert to space and title case)
    if (voucherType.includes('_')) {
      return voucherType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    // Return as-is for already formatted names
    return voucherType;
  };

  // State similar to PartyLedger.tsx
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [voucherType, setVoucherType] = useState<string>('all');

  // Pagination state for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 100;

  // Scroll button states (matching PartyLedger.tsx)
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  // Filter handlers (matching PartyLedger.tsx pattern)
  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
    setCurrentPage(1);
    setAllTransactions([]);
    setHasMore(true);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Fetch initial data when filters change
  useEffect(() => {
    const fetchData = async () => {
      if (!filters) {
        return;
      }

      setIsLoading(true);
      setAllTransactions([]);
      setCurrentPage(1);
      setHasMore(true);

      try {
        // Parse filter string to extract date range
        const urlParams = new URLSearchParams(filters);
        const startDate = urlParams.get('start');
        const endDate = urlParams.get('end');
        const clientId = urlParams.get('filter');

        if (!startDate || !endDate) {
          return;
        }

        // Fetch first page
        const params = {
          accountId: selectedAccount._id,
          partyType: 'all' as 'client' | 'supplier' | 'all', // Fetch both clients and suppliers
          from: startDate,
          to: endDate,
          page: 1,
          limit: rowsPerPage,
          voucherType,
          partyId: clientId || 'all'
        };

        const response = await dispatch(fetchAllPartiesLedgerTransactions(params)).unwrap();
        setAllTransactions(response.data.transactions || []);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
        setHasMore(response.data.pagination?.hasMore || false);
        setCurrentPage(1);
      } catch (error: any) {
        console.error('Error fetching ledger:', error);
        setAllTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, filters, voucherType, selectedAccount._id]);

  // Load more data on scroll
  const loadMoreData = async () => {
    if (!hasMore || isLoadingMore || !filters) return;

    setIsLoadingMore(true);
    try {
      const urlParams = new URLSearchParams(filters);
      const startDate = urlParams.get('start');
      const endDate = urlParams.get('end');
      const clientId = urlParams.get('filter');

      if (!startDate || !endDate) return;

      const nextPage = currentPage + 1;
      const params = {
        accountId: selectedAccount._id,
        partyType: 'all' as 'client' | 'supplier' | 'all', // Fetch both clients and suppliers
        from: startDate,
        to: endDate,
        page: nextPage,
        limit: rowsPerPage,
        voucherType,
        partyId: clientId || 'all'
      };

      const response = await dispatch(fetchAllPartiesLedgerTransactions(params)).unwrap();
      const newTransactions = response.data.transactions || [];

      setAllTransactions(prev => [...prev, ...newTransactions]);
      setHasMore(response.data.pagination?.hasMore || false);
      setCurrentPage(nextPage);
    } catch (error: any) {
      console.error('Error loading more data:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle scroll event with infinite scroll
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    // Determine scroll direction for button
    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }

    lastScrollTop.current = currentScrollTop;

    // Show/hide scroll button
    if (currentScrollTop > 100 || currentScrollTop < scrollHeight - clientHeight - 100) {
      setShowScrollButton(true);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 2000);
    }

    // Infinite scroll - load more when near bottom
    const scrolledToBottom = scrollHeight - currentScrollTop - clientHeight < 200;
    if (scrolledToBottom && hasMore && !isLoadingMore) {
      loadMoreData();
    }
  };

  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;

    const scrollHeight = scrollContainerRef.current.scrollHeight;

    if (scrollDirection === 'down') {
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    } else {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
            {/* Report Header */}
            <ReusableReportHeader
              reportType="PARTY_LEDGER_JOURNAL"
              title="Party Ledger (Journal Entries)"
              subtitle={(filterData && filterData.periodString) ? filterData.periodString : ''}
              recordCount={totalRecords}
              isLoading={isLoading}
              showPrint={false}
              showShare={false}
            />

            {/* Filters and Summary Section */}
            <Grid container px={2} py={1.5}>
              <Grid item xs={12} md={6}>
                <Filters
                  type={VocuherCategory.SALE}
                  filterHandler={filterOnChangeHandler}
                  selectedFilters={filterdOnChangeHandler}
                  hideFilter={true}
                />
              </Grid>

              {/* Right side - Voucher Type Filter */}
              <Grid item xs={12} md={6} style={{ textAlign: 'right', paddingLeft: '16px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Voucher Type</InputLabel>
                    <Select
                      value={voucherType}
                      label="Voucher Type"
                      onChange={(e) => setVoucherType(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="Invoice">Invoice</MenuItem>
                      <MenuItem value="Purchase Invoice">Purchase Invoice</MenuItem>
                      <MenuItem value="Receipt">Receipt</MenuItem>
                      <MenuItem value="Payment">Payment</MenuItem>
                      <MenuItem value="Credit Note">Credit Note</MenuItem>
                      <MenuItem value="Debit Note">Debit Note</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {(filterData && filterData.selectedClient) && (
                  <Typography style={{ fontSize: 14, fontWeight: 400, marginBottom: '8px' }}>
                    <strong style={{ paddingRight: 10 }}>Filtered Party:</strong>{filterData.selectedClient.name}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {allTransactions.length > 0 ? (
              <div>
                <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                  <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '13%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell>Party Name</StyledTableCell>
                      <StyledTableCell>Voucher Type</StyledTableCell>
                      <StyledTableCell>Voucher No.</StyledTableCell>
                      <StyledTableCell>Particulars</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Debit</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Credit</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allTransactions.map((row: any, index: number) => (
                      <StyledTableRow key={`${row._id}-${index}`}>
                        <StyledTableCell>
                          {moment(row.date).format("DD MMM YYYY")}
                        </StyledTableCell>
                        <StyledTableCell style={{ fontWeight: '600' }}>
                          {row.partyName}
                        </StyledTableCell>
                        <StyledTableCell>
                          {row.voucherType ? (
                            <Chip
                              label={formatVoucherType(row.voucherType)}
                              size="small"
                              variant="outlined"
                              style={{
                                fontSize: '8pt',
                                height: '20px',
                                borderColor:
                                  row.voucherType === 'Invoice' ? '#3b82f6' :
                                  row.voucherType === 'Sale Invoice' ? '#3b82f6' :
                                  row.voucherType === 'Purchase Invoice' ? '#dc2626' :
                                  row.voucherType === 'Receipt' ? '#059669' :
                                  row.voucherType === 'Payment' ? '#7c2d12' :
                                  row.voucherType === 'Credit Note' ? '#f59e0b' :
                                  row.voucherType === 'Debit Note' ? '#c026d3' :
                                  row.voucherType === 'EXPENSE_PAYMENT' ? '#16a34a' :
                                  '#6b7280'
                              }}
                            />
                          ) : (
                            <Chip
                              label="Opening Balance"
                              size="small"
                              variant="outlined"
                              style={{
                                fontSize: '8pt',
                                height: '20px',
                                borderColor: '#9333ea',
                                color: '#9333ea'
                              }}
                            />
                          )}
                        </StyledTableCell>
                        <StyledTableCell>{row.voucherNumber}</StyledTableCell>
                        <StyledTableCell>{row.particulars}</StyledTableCell>
                        <StyledTableCell style={{
                          color: row.debit > 0 ? '#d32f2f' : '#757575',
                          textAlign: 'right',
                          fontWeight: row.debit > 0 ? '600' : 'normal'
                        }}>
                          {row.debit > 0 ? Utils.formatNumber(row.debit.toFixed(2)) : ''}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          color: row.credit > 0 ? '#2e7d32' : '#757575',
                          textAlign: 'right',
                          fontWeight: row.credit > 0 ? '600' : 'normal'
                        }}>
                          {row.credit > 0 ? Utils.formatNumber(row.credit.toFixed(2)) : ''}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                    {isLoadingMore && (
                      <StyledTableRow>
                        <StyledTableCell colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                          <CircularProgress size={24} color="info" />
                          <Typography variant="body2" style={{ marginTop: '10px' }}>Loading more...</Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : !isLoading ? (
              <Grid container p={2}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                  <Typography>No Data Found</Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container p={2}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress color="info" />
                </Grid>
              </Grid>
            )}
          </Box>


          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 46,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PartyLedgerJournal;
